import chalk from "chalk";
import { log } from "@/cli/log";
import { input } from "@inquirer/prompts";
import { readFileSync, writeFileSync, existsSync, mkdirSync } from "node:fs";
import { join } from "node:path";
import { homedir } from "os";
import { cliAuthClient } from "@/cli/auth-client";
import { verbose } from "@/config";

// Define types for our auth session
type User = {
  id: string;
  email: string;
  [key: string]: any;
};

// Auth store for persisting session data
const STORAGE_DIR = join(homedir(), ".beeps");
const AUTH_FILE = join(STORAGE_DIR, "auth.json");

class AuthStore {
  private _token: string = "";
  private _user: User | null = null;

  constructor() {
    // Ensure storage directory exists
    if (!existsSync(STORAGE_DIR)) {
      mkdirSync(STORAGE_DIR, { recursive: true });
    }

    // Load initial state if exists
    if (existsSync(AUTH_FILE)) {
      try {
        const data = JSON.parse(readFileSync(AUTH_FILE, "utf-8")) as {
          token: string;
          user: User | null;
        };
        if (data.token && data.user) {
          this._token = data.token;
          this._user = data.user;
        }
      } catch (error) {
        console.error("Failed to load auth state:", error);
      }
    }
  }

  get token(): string {
    return this._token;
  }

  get user(): User | null {
    return this._user;
  }

  get isValid(): boolean {
    return !!this._token && !!this._user;
  }

  get email(): string | undefined {
    return this._user?.email;
  }

  save(token: string, user: User | null): void {
    this._token = token;
    this._user = user;

    try {
      writeFileSync(
        AUTH_FILE,
        JSON.stringify(
          {
            token,
            user,
          },
          null,
          2,
        ),
      );
    } catch (error) {
      console.error("Failed to save auth state:", error);
    }
  }

  clear(): void {
    this._token = "";
    this._user = null;

    try {
      if (existsSync(AUTH_FILE)) {
        writeFileSync(
          AUTH_FILE,
          JSON.stringify(
            {
              token: "",
              user: null,
            },
            null,
          ),
        );
      }
    } catch (error) {
      console.error("Failed to clear auth state:", error);
    }
  }
}

// Create a singleton instance of the auth store
const authStore = new AuthStore();

export async function handleLogin(): Promise<{
  token: string;
  userId: string;
  email: string;
}> {
  log.title("\nWelcome!");

  const email = await input({
    message: "Enter your email:",
    validate: (value) => {
      if (!value.includes("@")) {
        return "Please enter a valid email address";
      }
      return true;
    },
  });

  try {
    log.info(`\nSending login code to ${chalk.bold(email)}...`);

    // Request OTP email using Better Auth
    const sendResponse = await cliAuthClient.emailOtp.sendVerificationOtp({
      email,
      type: "sign-in",
    });

    verbose &&
      console.log("Send response:", JSON.stringify(sendResponse, null, 2));

    const { error: sendError } = sendResponse;

    if (sendError) {
      throw new Error(`Failed to send verification code: ${sendError.message}`);
    }

    log.success(`\nA login code has been sent to ${chalk.bold(email)}`);

    const code = await input({
      message: "Enter the login code:",
    });

    // Verify OTP code using the signIn.emailOtp method
    const { data, error } = await cliAuthClient.signIn.emailOtp({
      email,
      otp: code,
    });

    if (error || !data) {
      throw new Error(
        `Failed to verify login code: ${error?.message || "Unknown error"}`,
      );
    }

    // The data structure might be different based on the Better Auth version
    // Assuming data contains user and token information
    const token = data.token || "";
    const user = data.user || null;

    // Save the session to our file store
    authStore.save(token, user);

    log.success("\n✨ Successfully logged in!");
    return {
      token,
      userId: user?.id || "",
      email: user?.email || "",
    };
  } catch (error) {
    if (error instanceof Error) {
      log.error(`\nLogin failed: ${error.message}`);
      console.error("Full error:", error);
    } else {
      log.error("\nLogin failed: Unknown error");
      console.error("Full error:", error);
    }
    process.exit(1);
  }
}

export async function ensureLoggedIn(): Promise<{
  token: string;
  userId: string;
  email: string;
}> {
  try {
    // Check if we have a valid session in our file store
    if (!authStore.isValid) {
      log.warning("You need to login first");
      return handleLogin();
    }

    // Try to validate the session using the CLI auth client
    try {
      // Use the Better Auth session endpoint
      const { data, error } = await cliAuthClient.getSession();

      if (error || !data) {
        throw new Error("Session invalid");
      }

      // If the session is valid, we can update the user data if needed
      if (data.user && data.user.id !== authStore.user?.id) {
        authStore.save(authStore.token, data.user);
      }
    } catch (error) {
      log.warning("Your session has expired. Please login again.");
      authStore.clear();
      return handleLogin();
    }

    return {
      token: authStore.token,
      userId: authStore.user?.id || "",
      email: authStore.user?.email || "",
    };
  } catch (error) {
    log.warning("Authentication error. Please login again.");
    return handleLogin();
  }
}
